import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import {
  dossiers,
  type ApercuChangement,
  type Creation,
  type EtatFormules,
  type LigneFormule,
  type PlanVue,
} from '../../lib/dossiers';
import { PaiementStripe } from '../../components/PaiementStripe';
import { BlocFacturation, type SuggestionFacturation } from './BlocFacturation';

interface Props {
  creation: Creation;
  onPret: () => void;
  onErreur: (message: string | null) => void;
  /** L'identité du dossier, proposée comme société facturée (29/10/2026). */
  suggestion?: SuggestionFacturation | null;
}

/** Ce que l'on s'apprête à modifier sur un abonnement en cours (formule ou quantité). */
interface ChangementEnCours {
  priceId: string;
  quantite: number;
  apercu: ApercuChangement;
}

const euros = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: n % 1 ? 2 : 0 });

const message = (e: unknown, defaut: string) => (e instanceof ApiError ? e.message : defaut);

/** Après un paiement, le webhook Stripe ouvre les droits : quelques secondes d'attente. */
const ATTENTE_WEBHOOK_MS = 45_000;
const INTERVALLE_MS = 2_500;

/**
 * Étape « Formules » (11/10/2026).
 *
 * Pour chaque produit coché : formule en cours, formule à choisir, ou dossier
 * de plus à ajouter. Un paiement par produit ou par offre groupée (Stripe
 * embarqué) ; le retour de Stripe ramène ici avec `?paiement=` et l'écran
 * attend que le webhook ait ouvert les droits. On ne continue que lorsque tous
 * les produits sont couverts.
 */
export function EtapeFormules({ creation, onPret, onErreur, suggestion }: Props) {
  const [etat, setEtat] = useState<EtatFormules | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [changement, setChangement] = useState<ChangementEnCours | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [attente, setAttente] = useState(false);
  const [code, setCode] = useState('');
  const debutAttente = useRef<number | null>(null);

  const charger = useCallback(async () => {
    try {
      const e = await dossiers.formules(creation.id);
      setEtat(e);
      return e;
    } catch (e) {
      onErreur(message(e, 'Les formules n’ont pas pu être chargées.'));
      return null;
    }
  }, [creation.id, onErreur]);

  // Retour de Stripe : on attend le webhook, puis on nettoie l'adresse.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('paiement')) {
      void charger();
      return;
    }
    params.delete('paiement');
    const reste = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${reste ? `?${reste}` : ''}`);
    setAttente(true);
    debutAttente.current = Date.now();
    void charger();
  }, [charger]);

  useEffect(() => {
    if (!attente) return;
    if (etat?.pret) {
      setAttente(false);
      return;
    }
    const minuterie = window.setTimeout(() => {
      if (debutAttente.current && Date.now() - debutAttente.current > ATTENTE_WEBHOOK_MS) {
        setAttente(false);
        onErreur(
          'Le paiement n’est pas encore confirmé. Patientez quelques instants puis rechargez la page ; aucun second paiement n’est nécessaire.',
        );
        return;
      }
      void charger();
    }, INTERVALLE_MS);
    return () => window.clearTimeout(minuterie);
  }, [attente, etat, charger, onErreur]);

  const souscrire = async (plan: PlanVue, ligne?: LigneFormule) => {
    if (!plan.stripePriceId) return;
    onErreur(null);
    // Abonnement Stripe déjà en cours sur ce produit : on le modifie (prorata).
    if (ligne?.changement) {
      await preparerChangement(plan.stripePriceId, ligne.formuleActuelle?.quantite ?? 1);
      return;
    }
    setOccupe(plan.id);
    try {
      const r = await dossiers.payer(creation.id, { priceId: plan.stripePriceId, code: code.trim() || null });
      // L'espace a déjà son abonnement : le produit en devient une ligne, au
      // prorata jusqu'à l'échéance commune — un aperçu à confirmer, pas un paiement.
      if ('ajout' in r) setChangement({ priceId: plan.stripePriceId, quantite: r.apercu.quantity, apercu: r.apercu });
      else setClientSecret(r.clientSecret);
    } catch (e) {
      onErreur(message(e, 'Le paiement n’a pas pu démarrer.'));
    } finally {
      setOccupe(null);
    }
  };

  const preparerChangement = async (priceId: string, quantite: number) => {
    onErreur(null);
    setOccupe(priceId);
    try {
      const apercu = await dossiers.apercuChangement(creation.id, { priceId, quantite });
      setChangement({ priceId, quantite, apercu });
    } catch (e) {
      onErreur(message(e, 'Le changement n’a pas pu être calculé.'));
    } finally {
      setOccupe(null);
    }
  };

  const confirmerChangement = async () => {
    if (!changement) return;
    setOccupe('changement');
    try {
      await dossiers.changer(creation.id, {
        priceId: changement.priceId,
        quantite: changement.quantite,
        prorationDate: changement.apercu.prorationDate,
      });
      setChangement(null);
      await charger();
    } catch (e) {
      onErreur(message(e, 'Le changement n’a pas abouti.'));
    } finally {
      setOccupe(null);
    }
  };

  if (!etat) return <p role="status">Chargement des formules…</p>;

  if (clientSecret) {
    return (
      <>
        <p>Paiement sécurisé par Stripe. Vous reviendrez ici une fois le paiement validé.</p>
        <PaiementStripe clientSecret={clientSecret} />
        <div className="dos-actions">
          <button type="button" className="btn btn-secondaire" onClick={() => setClientSecret(null)}>
            Retour aux formules
          </button>
        </div>
      </>
    );
  }

  const profil = etat.facturation?.profil ?? null;
  const bloque = !etat.gestionnaire
    ? 'Seuls le propriétaire et les administrateurs de cet espace choisissent les formules.'
    : !etat.venteOuverte
      ? 'La souscription aux formules payantes n’est pas encore ouverte.'
      : !profil
        ? 'Indiquez d’abord à qui facturer : les formules payantes se débloquent ensuite.'
        : null;
  const echeance = etat.facturation?.echeance ? new Date(etat.facturation.echeance).toLocaleDateString('fr-FR') : null;

  const carteFormule = (plan: PlanVue, ligne?: LigneFormule) => {
    const actuelle = ligne?.formuleActuelle?.planId === plan.id;
    const payante = Boolean(plan.stripePriceId);
    return (
      <li key={plan.id} className={`dos-option${actuelle ? ' is-actif' : ''}`}>
        <span>
          <strong>{plan.name}</strong>
          {plan.includes.length > 0 ? <small> — inclut {plan.includes.join(', ')}</small> : null}
        </span>
        <small>{payante ? `${euros(plan.priceMonthly)} / mois HT` : 'Gratuit'}</small>
        {plan.description ? <small>{plan.description}</small> : null}
        {actuelle ? (
          <small>Formule actuelle</small>
        ) : payante ? (
          <button
            type="button"
            className="btn"
            disabled={Boolean(bloque) || occupe !== null}
            onClick={() => void souscrire(plan, ligne)}
          >
            {occupe === plan.id || occupe === plan.stripePriceId ? '…' : ligne?.changement ? 'Passer à cette formule' : 'Souscrire'}
          </button>
        ) : null}
      </li>
    );
  };

  return (
    <>
      {etat.facturation.espace ? (
        <p className="dos-aide">
          Espace : <strong>{etat.facturation.espace}</strong>
        </p>
      ) : null}
      <BlocFacturation
        spaceId={etat.facturation.spaceId}
        profil={profil}
        modifiable={etat.gestionnaire}
        suggestion={suggestion}
        onEnregistre={() => void charger()}
        autreSociete={etat.gestionnaire}
      />
      {echeance ? (
        <p className="dos-aide">
          Votre espace est facturé chaque mois le {echeance.slice(0, 2)} : un produit ajouté rejoint cette facture, au
          prorata jusqu’au {echeance}.
        </p>
      ) : null}

      {attente ? (
        <p role="status" className="dos-aide">
          Paiement reçu, activation de vos droits en cours…
        </p>
      ) : null}
      {bloque ? (
        <p className="dos-erreur" role="alert">
          {bloque}
        </p>
      ) : null}

      {changement ? (
        <section className="dos-recap" aria-labelledby="dos-changement">
          <h3 id="dos-changement">
            {changement.apercu.ajout ? 'Ajouter' : 'Modifier votre abonnement'} {changement.apercu.plan.name}
            {changement.apercu.ajout ? ' à l’abonnement de votre espace' : ''}
          </h3>
          {changement.apercu.unchanged ? (
            <p>Aucun changement : c’est déjà votre abonnement.</p>
          ) : (
            <>
              <p>
                {changement.apercu.quantity > 1 ? `${changement.apercu.quantity} dossiers — ` : ''}
                {euros(changement.apercu.monthlyAfter)} / mois HT ensuite.
              </p>
              <p>
                {(changement.apercu.prorata ?? 0) >= 0
                  ? `Prorata jusqu’à l’échéance : ${euros(changement.apercu.prorata ?? 0)}, ajouté à votre prochaine facture`
                  : `Crédit de ${euros(-(changement.apercu.prorata ?? 0))} déduit de votre prochaine facture`}
                {changement.apercu.currentPeriodEnd
                  ? ` du ${new Date(changement.apercu.currentPeriodEnd).toLocaleDateString('fr-FR')}.`
                  : '.'}{' '}
                Rien n’est prélevé aujourd’hui.
              </p>
            </>
          )}
          <div className="dos-actions">
            <button type="button" className="btn btn-secondaire" onClick={() => setChangement(null)}>
              Annuler
            </button>
            {!changement.apercu.unchanged ? (
              <button type="button" className="btn" disabled={occupe !== null} onClick={() => void confirmerChangement()}>
                {occupe === 'changement' ? 'Modification…' : 'Confirmer'}
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {etat.offresGroupees.length > 0 ? (
        <section aria-labelledby="dos-offres">
          <h3 id="dos-offres">Offres groupées</h3>
          <ul className="dos-choix dos-choix--liste">{etat.offresGroupees.map((p) => carteFormule(p))}</ul>
        </section>
      ) : null}

      {etat.lignes.map((ligne) => (
        <section key={ligne.produit} aria-labelledby={`dos-formule-${ligne.produit}`}>
          <h3 id={`dos-formule-${ligne.produit}`}>
            {ligne.nom}{' '}
            <span className="dos-badge">
              {ligne.etat === 'OK' ? 'Couvert' : ligne.etat === 'QUOTA_ATTEINT' ? 'Quota atteint' : 'Formule à choisir'}
            </span>
          </h3>

          {ligne.etat === 'OK' ? (
            <p>
              {ligne.formuleActuelle?.nom ? `Formule ${ligne.formuleActuelle.nom}` : 'Accès ouvert'}
              {typeof ligne.quota.limite === 'number'
                ? ` — ${ligne.quota.utilises} / ${ligne.quota.limite} dossier(s) utilisé(s).`
                : '.'}
            </p>
          ) : null}

          {ligne.etat === 'QUOTA_ATTEINT' ? (
            <>
              <p>
                Votre formule {ligne.formuleActuelle?.nom ?? ''} compte {ligne.quota.limite} dossier(s), tous utilisés.
              </p>
              {ligne.ajoutDossier ? (
                <div className="dos-actions">
                  <button
                    type="button"
                    className="btn"
                    disabled={Boolean(bloque) || occupe !== null}
                    onClick={() =>
                      void preparerChangement(ligne.ajoutDossier!.stripePriceId, ligne.ajoutDossier!.quantiteActuelle + 1)
                    }
                  >
                    Ajouter un dossier à mon abonnement
                  </button>
                </div>
              ) : (
                <p className="dos-aide">Choisissez une formule supérieure :</p>
              )}
            </>
          ) : null}

          {ligne.etat === 'A_CHOISIR' || (ligne.etat === 'QUOTA_ATTEINT' && !ligne.ajoutDossier) ? (
            ligne.formules.length > 0 ? (
              <ul className="dos-choix dos-choix--liste">{ligne.formules.map((p) => carteFormule(p, ligne))}</ul>
            ) : (
              <p className="dos-aide">Aucune formule n’est encore proposée pour ce produit.</p>
            )
          ) : null}
        </section>
      ))}

      {etat.lignes.some((l) => l.etat === 'A_CHOISIR') ? (
        <div className="dos-question">
          <label htmlFor="dos-code">Code de réduction (facultatif)</label>
          <input id="dos-code" value={code} maxLength={60} onChange={(e) => setCode(e.target.value)} />
        </div>
      ) : null}

      <div className="dos-actions">
        <button type="button" className="btn" disabled={!etat.pret || attente} onClick={onPret}>
          Continuer
        </button>
      </div>
    </>
  );
}
